<div class="flex flex-col w-full min-h-full bg-gray-50 dark:bg-gray-900">

    <header class="sticky top-0 z-30 bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div class="px-4 sm:px-6 py-4 max-w-5xl mx-auto w-full">
            <div class="flex items-center gap-3">
                <div class="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/40">
                    <mat-icon class="text-amber-700 dark:text-amber-300">card_giftcard</mat-icon>
                </div>
                <div>
                    <h1 class="text-xl sm:text-2xl font-bold tracking-tight">Free Subscription QR</h1>
                    <p class="text-xs sm:text-sm text-gray-500">Generate promotional QR codes for free subscriptions</p>
                </div>
            </div>
        </div>
    </header>

    <mat-progress-bar *ngIf="isGenerating" mode="indeterminate" class="fixed top-0 left-0 right-0 z-50"></mat-progress-bar>

    <div class="flex-1 w-full max-w-5xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">

        <mat-card class="p-5 sm:p-6 rounded-2xl shadow-sm">
            <h2 class="text-lg font-bold mb-1">Configuration</h2>
            <p class="text-sm text-gray-500 mb-5">Set the parameters for the free subscription QR code.</p>

            <form [formGroup]="form" (ngSubmit)="generateQr()" class="flex flex-col gap-4">

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <mat-form-field appearance="outline" class="w-full">
                        <mat-label>Number of Subscriptions</mat-label>
                        <input matInput type="number" formControlName="vi_subscriptions" min="1" max="10000">
                        <mat-icon matPrefix>confirmation_number</mat-icon>
                        <mat-hint>Total uses available</mat-hint>
                        <mat-error *ngIf="form.get('vi_subscriptions')?.hasError('required')">Required</mat-error>
                        <mat-error *ngIf="form.get('vi_subscriptions')?.hasError('min')">Must be at least 1</mat-error>
                        <mat-error *ngIf="form.get('vi_subscriptions')?.hasError('max')">Maximum 10000</mat-error>
                    </mat-form-field>

                    <mat-form-field appearance="outline" class="w-full">
                        <mat-label>Center Text (visual only)</mat-label>
                        <input matInput formControlName="ds_centerText" maxlength="60"
                               (blur)="refreshCenterText()">
                        <mat-icon matPrefix>title</mat-icon>
                        <mat-hint>Displayed inside the QR code</mat-hint>
                    </mat-form-field>
                </div>

                <mat-form-field appearance="outline" class="w-full">
                    <mat-label>Internal Description / Reason</mat-label>
                    <textarea matInput formControlName="ds_description" rows="2" maxlength="255"
                              placeholder="e.g. Trade show giveaway - Madrid 2025"></textarea>
                    <mat-icon matPrefix>description</mat-icon>
                    <mat-hint align="end">{{ form.get('ds_description')?.value?.length || 0 }}/255</mat-hint>
                    <mat-error *ngIf="form.get('ds_description')?.hasError('required')">Description is required</mat-error>
                    <mat-error *ngIf="form.get('ds_description')?.hasError('minlength')">At least 3 characters</mat-error>
                </mat-form-field>

                <div class="flex flex-col sm:flex-row gap-3 sm:justify-end pt-2">
                    <button type="button" mat-stroked-button (click)="resetForm()" *ngIf="qrResponse">
                        <mat-icon>refresh</mat-icon>
                        <span class="ml-1">Reset</span>
                    </button>
                    <button type="submit" mat-flat-button color="primary"
                            [disabled]="form.invalid || isGenerating"
                            class="!h-12 sm:!h-10">
                        <mat-icon>qr_code_2</mat-icon>
                        <span class="ml-2">{{ isGenerating ? 'Generating...' : 'Generate QR Codes' }}</span>
                    </button>
                </div>
            </form>
        </mat-card>

        <ng-container *ngIf="qrResponse">

            <div class="grid grid-cols-2 gap-3 sm:gap-4">
                <div class="bg-white dark:bg-card p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-transparent">
                    <div class="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider">
                        <mat-icon class="icon-size-4">redeem</mat-icon>
                        Uses Left
                    </div>
                    <div class="text-2xl sm:text-3xl font-bold mt-1 text-emerald-600 dark:text-emerald-400">
                        {{ qrResponse.vi_uses_left }}
                    </div>
                </div>
                <div class="bg-white dark:bg-card p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-transparent">
                    <div class="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider">
                        <mat-icon class="icon-size-4">schedule</mat-icon>
                        Expires
                    </div>
                    <div class="text-base sm:text-xl font-bold mt-1 text-amber-600 dark:text-amber-400">
                        {{ qrResponse.dt_expiration.datetime.date | date:'mediumDate' }}
                    </div>
                    <div class="text-xs text-gray-400 mt-0.5">
                        {{ qrResponse.dt_expiration.datetime.date | date:'shortTime' }}
                    </div>
                </div>
            </div>

            <mat-card class="rounded-2xl shadow-sm overflow-hidden">
                <mat-tab-group animationDuration="200ms" mat-stretch-tabs="false" class="qr-tabs">

                    <mat-tab>
                        <ng-template mat-tab-label>
                            <mat-icon class="mr-2">smartphone</mat-icon>
                            <span>Mobile App</span>
                        </ng-template>

                        <div class="flex flex-col lg:flex-row items-center gap-6 p-4 sm:p-6">
                            <div class="bg-white p-3 sm:p-4 border rounded-2xl shadow-sm flex-shrink-0">
                                <ngx-qrcode-styling #mobileQrCode [config]="mobileQrConfig"></ngx-qrcode-styling>
                            </div>

                            <div class="flex flex-col gap-3 w-full lg:max-w-md">
                                <h3 class="text-lg font-bold">Mobile App Link</h3>
                                <p class="text-sm text-gray-500">Scan to open the app and redeem the free subscription.</p>

                                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <button mat-stroked-button (click)="openZoomedQr(zoomMobile)" class="!min-w-0">
                                        <mat-icon>zoom_in</mat-icon>
                                    </button>
                                    <button mat-stroked-button (click)="downloadQr('mobile')" class="!min-w-0">
                                        <mat-icon>download</mat-icon>
                                    </button>
                                    <button mat-stroked-button (click)="copyUrl(qrResponse?.ds_App_url)" class="!min-w-0">
                                        <mat-icon>content_copy</mat-icon>
                                    </button>
                                    <button mat-flat-button color="primary" (click)="shareQr('mobile')" class="!min-w-0"
                                            [matTooltip]="canShareFiles ? 'Share QR' : 'Will download (no share support)'">
                                        <mat-icon>{{ canShareFiles ? 'share' : 'ios_share' }}</mat-icon>
                                    </button>
                                </div>

                                <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border text-xs break-all cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                     (click)="copyUrl(qrResponse?.ds_App_url)" matTooltip="Tap to copy">
                                    {{ qrResponse?.ds_App_url }}
                                </div>
                            </div>
                        </div>
                    </mat-tab>

                    <mat-tab>
                        <ng-template mat-tab-label>
                            <mat-icon class="mr-2">desktop_windows</mat-icon>
                            <span>Desktop Web</span>
                        </ng-template>

                        <div class="flex flex-col lg:flex-row items-center gap-6 p-4 sm:p-6">
                            <div class="bg-white p-3 sm:p-4 border rounded-2xl shadow-sm flex-shrink-0">
                                <ngx-qrcode-styling #desktopQrCode [config]="desktopQrConfig"></ngx-qrcode-styling>
                            </div>

                            <div class="flex flex-col gap-3 w-full lg:max-w-md">
                                <h3 class="text-lg font-bold">Desktop Web Link</h3>
                                <p class="text-sm text-gray-500">Scan to open the website and redeem the free subscription.</p>

                                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    <button mat-stroked-button (click)="openZoomedQr(zoomDesktop)" class="!min-w-0">
                                        <mat-icon>zoom_in</mat-icon>
                                    </button>
                                    <button mat-stroked-button (click)="downloadQr('desktop')" class="!min-w-0">
                                        <mat-icon>download</mat-icon>
                                    </button>
                                    <button mat-stroked-button (click)="copyUrl(qrResponse?.ds_Web_url)" class="!min-w-0">
                                        <mat-icon>content_copy</mat-icon>
                                    </button>
                                    <button mat-flat-button color="primary" (click)="shareQr('desktop')" class="!min-w-0"
                                            [matTooltip]="canShareFiles ? 'Share QR' : 'Will download (no share support)'">
                                        <mat-icon>{{ canShareFiles ? 'share' : 'ios_share' }}</mat-icon>
                                    </button>
                                </div>

                                <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border text-xs break-all cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                                     (click)="copyUrl(qrResponse?.ds_Web_url)" matTooltip="Tap to copy">
                                    {{ qrResponse?.ds_Web_url }}
                                </div>
                            </div>
                        </div>
                    </mat-tab>

                </mat-tab-group>
            </mat-card>

            <div class="hidden-offscreen absolute -left-[9999px] -top-[9999px]">
                <ngx-qrcode-styling #bigMobileQrCode [config]="bigMobileQrConfig"></ngx-qrcode-styling>
                <ngx-qrcode-styling #bigDesktopQrCode [config]="bigDesktopQrConfig"></ngx-qrcode-styling>
            </div>

        </ng-container>

        <div *ngIf="!qrResponse && !isGenerating" class="flex flex-col items-center justify-center text-center p-10 opacity-60">
            <mat-icon class="icon-size-16 mb-4 text-gray-400">qr_code_scanner</mat-icon>
            <p class="text-gray-500">Fill out the form and tap Generate to create QR codes.</p>
        </div>

    </div>
</div>

<ng-template #zoomMobile>
    <div class="bg-white p-4 sm:p-6 rounded-xl flex flex-col items-center shadow-2xl">
        <h3 class="text-xl font-bold mb-4">Mobile App QR</h3>
        <div class="responsive-qr-container max-w-[80vw] max-h-[70vh]">
            <ngx-qrcode-styling [config]="bigMobileQrConfig"></ngx-qrcode-styling>
        </div>
        <button mat-button (click)="_dialog.closeAll()" class="mt-4">Close</button>
    </div>
</ng-template>

<ng-template #zoomDesktop>
    <div class="bg-white p-4 sm:p-6 rounded-xl flex flex-col items-center shadow-2xl">
        <h3 class="text-xl font-bold mb-4">Desktop Web QR</h3>
        <div class="responsive-qr-container max-w-[80vw] max-h-[70vh]">
            <ngx-qrcode-styling [config]="bigDesktopQrConfig"></ngx-qrcode-styling>
        </div>
        <button mat-button (click)="_dialog.closeAll()" class="mt-4">Close</button>
    </div>
</ng-template>
